import { supabase } from '../lib/supabase'
import { 
  CoordinatorUpgrade, 
  CoordinatorCredits, 
  CoordinatorProfile, 
  CoordinatorUpgradeRequest 
} from '../lib/supabase'
import { generatePayFastSignature } from '../utils/payfastSignature'

export class CoordinatorUpgradeService {
  private static readonly UPGRADE_COST = 300.00
  private static readonly CURRENCY = 'ZAR'
  
  // Admin PayFast Configuration (for coordinator upgrade payments to admin)
  private static readonly ADMIN_PAYFAST_MERCHANT_ID = process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_ID || '10000100'
  private static readonly ADMIN_PAYFAST_MERCHANT_KEY = process.env.REACT_APP_ADMIN_PAYFAST_MERCHANT_KEY || '46f0cd694581a'
  private static readonly ADMIN_PAYFAST_PASSPHRASE = process.env.REACT_APP_ADMIN_PAYFAST_PASSPHRASE || ''
  
  // PayFast URL based on environment
  private static readonly PAYFAST_ENVIRONMENT = process.env.REACT_APP_PAYFAST_ENVIRONMENT || 'sandbox'
  private static readonly PAYFAST_SANDBOX_URL = process.env.REACT_APP_PAYFAST_SANDBOX_URL || 'https://sandbox.payfast.co.za/eng/process'
  private static readonly PAYFAST_PRODUCTION_URL = process.env.REACT_APP_PAYFAST_PRODUCTION_URL || 'https://www.payfast.co.za/eng/process'
  
  private static get PAYFAST_URL(): string {
    return this.PAYFAST_ENVIRONMENT === 'production' ? this.PAYFAST_PRODUCTION_URL : this.PAYFAST_SANDBOX_URL
  }

  /**
   * Check if user is already a coordinator
   */
  static async isUserCoordinator(userEmail: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('coordinator_profiles')
        .select('id, status')
        .eq('user_email', userEmail)
        .eq('status', 'active')
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      return !!data
    } catch (error) {
      console.error('Error checking coordinator status:', error)
      return false
    }
  }

  /**
   * Get coordinator profile for user
   */
  static async getCoordinatorProfile(userEmail: string): Promise<CoordinatorProfile | null> {
    try {
      const { data, error } = await supabase
        .from('coordinator_profiles')
        .select('*')
        .eq('user_email', userEmail)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      return data
    } catch (error) {
      console.error('Error fetching coordinator profile:', error)
      return null
    }
  }

  /**
   * Get available event creation credits for user
   */
  static async getAvailableCredits(userEmail: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('coordinator_credits')
        .select('credits_remaining')
        .eq('user_email', userEmail)

      if (error) {
        throw error
      }

      return data?.reduce((total, credit) => total + credit.credits_remaining, 0) || 0
    } catch (error) {
      console.error('Error fetching available credits:', error)
      return 0
    }
  }

  /**
   * Get all credits for user (for display purposes)
   */
  static async getUserCredits(userEmail: string): Promise<CoordinatorCredits[]> {
    try {
      const { data, error } = await supabase
        .from('coordinator_credits')
        .select('*')
        .eq('user_email', userEmail)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Error fetching user credits:', error)
      return []
    }
  }

  /**
   * Create coordinator upgrade payment request
   */
  static async createUpgradePayment(request: CoordinatorUpgradeRequest): Promise<{
    upgrade: CoordinatorUpgrade
    paymentUrl: string
  }> {
    console.log('=== CREATE UPGRADE PAYMENT SERVICE CALLED ===')
    console.log('Request received:', request)
    try {
      // Generate unique payment reference
      const paymentReference = `COORD_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Create upgrade record in database
      const { data: upgrade, error } = await supabase
        .from('coordinator_upgrades')
        .insert({
          user_email: request.user_email,
          user_name: request.user_name,
          payment_reference: paymentReference,
          payment_amount: this.UPGRADE_COST,
          currency: this.CURRENCY,
          payment_status: 'pending'
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      // MINIMAL FIELDS ONLY - Following PayFast guide Rule #1
      // Only include the absolutely required fields to avoid signature validation issues
      const payFastParams = {
        merchant_id: this.ADMIN_PAYFAST_MERCHANT_ID,
        merchant_key: this.ADMIN_PAYFAST_MERCHANT_KEY,
        return_url: request.return_url,
        cancel_url: request.cancel_url,
        notify_url: request.notify_url,
        name_first: request.user_name.split(' ')[0] || 'User',
        name_last: request.user_name.split(' ').slice(1).join(' ') || 'Name',
        email_address: request.user_email,
        m_payment_id: paymentReference,
        amount: this.UPGRADE_COST.toFixed(2),
        item_name: 'GOMTB Coordinator Upgrade',
      }
      
      // Submit PayFast payment form directly
      this.submitPayFastForm(payFastParams)

      return {
        upgrade,
        paymentUrl: 'FORM_SUBMITTED' // Indicate form was submitted
      }
    } catch (error) {
      console.error('Error creating upgrade payment:', error)
      throw new Error('Failed to create coordinator upgrade payment')
    }
  }

  /**
   * Process PayFast payment notification
   */
  static async processPaymentNotification(paymentData: any): Promise<boolean> {
    try {
      const paymentReference = paymentData.m_payment_id
      const paymentStatus = paymentData.payment_status
      const pfPaymentId = paymentData.pf_payment_id

      // Update upgrade record
      const { error } = await supabase
        .from('coordinator_upgrades')
        .update({
          payment_status: paymentStatus === 'COMPLETE' ? 'completed' : 'failed',
          payfast_payment_id: pfPaymentId,
          gateway_response: paymentData,
          updated_at: new Date().toISOString()
        })
        .eq('payment_reference', paymentReference)

      if (error) {
        throw error
      }

      return paymentStatus === 'COMPLETE'
    } catch (error) {
      console.error('Error processing payment notification:', error)
      return false
    }
  }

  /**
   * Use an event creation credit
   */
  static async useEventCredit(userEmail: string): Promise<boolean> {
    try {
      // Import authService dynamically to avoid circular dependencies
      const { authService } = await import('./authService');
      const currentUser = await authService.getCurrentUser();
      
      // Check if user is admin (admins don't consume credits)
      const isAdmin = currentUser?.role === 'admin';
      
      if (isAdmin) {
        console.log('Admin user - not consuming credits');
        return true; // Admin always succeeds without consuming credits
      }

      // Get user profile to get user_id
      const { data: profile } = await supabase
        .from('coordinator_profiles')
        .select('user_id')
        .eq('user_email', userEmail)
        .single()

      if (!profile) {
        throw new Error('Coordinator profile not found')
      }

      // Call the database function to use credit
      const { data, error } = await supabase
        .rpc('use_event_credit', { p_user_id: profile.user_id })

      if (error) {
        throw error
      }

      return data === true
    } catch (error) {
      console.error('Error using event credit:', error)
      return false
    }
  }

  /**
   * Get upgrade payment history for user
   */
  static async getUpgradeHistory(userEmail: string): Promise<CoordinatorUpgrade[]> {
    try {
      const { data, error } = await supabase
        .from('coordinator_upgrades')
        .select('*')
        .eq('user_email', userEmail)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      console.error('Error fetching upgrade history:', error)
      return []
    }
  }

  /**
   * Submit PayFast payment form directly
   */
  private static submitPayFastForm(params: Record<string, string>): void {
    // Validate required parameters
    const requiredParams = ['merchant_id', 'merchant_key', 'amount', 'item_name']
    const missingParams = requiredParams.filter(param => !params[param])
    
    if (missingParams.length > 0) {
      console.error('Missing required PayFast parameters:', missingParams)
      throw new Error(`Missing required parameters: ${missingParams.join(', ')}`)
    }
    
    // Debug environment variables
    console.log('Environment Variables Check:', {
      MERCHANT_ID: this.ADMIN_PAYFAST_MERCHANT_ID,
      MERCHANT_KEY: this.ADMIN_PAYFAST_MERCHANT_KEY ? 'SET' : 'NOT SET',
      PASSPHRASE: this.ADMIN_PAYFAST_PASSPHRASE ? 'SET' : 'NOT SET',
      ENVIRONMENT: this.PAYFAST_ENVIRONMENT,
      URL: this.PAYFAST_URL
    })
    
    // Validate merchant credentials
    if (!this.ADMIN_PAYFAST_MERCHANT_ID || this.ADMIN_PAYFAST_MERCHANT_ID === '10000100') {
      console.error('Invalid merchant ID - using default/placeholder value')
      throw new Error('PayFast merchant ID not properly configured')
    }
    
    const signature = generatePayFastSignature(params, this.ADMIN_PAYFAST_PASSPHRASE)
    
    // Debug logging
    console.log('PayFast Parameters:', {
      ...params,
      signature,
      environment: this.PAYFAST_ENVIRONMENT,
      url: this.PAYFAST_URL
    })
    
    // Create form element with proper attributes
    const form = document.createElement('form')
    form.id = 'payfast-payment-form'
    form.method = 'POST'
    form.action = this.PAYFAST_URL
    form.style.display = 'none'
    form.setAttribute('accept-charset', 'UTF-8')
    
    // Add all parameters as hidden inputs
    const formParams = { ...params, signature }
    Object.entries(formParams).forEach(([key, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = value.toString()
      form.appendChild(input)
    })
    
    // Add form to document and submit
    document.body.appendChild(form)
    form.submit()
  }

  /**
   * Check if user can create events (has available credits)
   */
  static async canCreateEvent(userEmail: string): Promise<{
    canCreate: boolean
    availableCredits: number
    isCoordinator: boolean
  }> {
    try {
      // Import authService dynamically to avoid circular dependencies
      const { authService } = await import('./authService');
      const currentUser = await authService.getCurrentUser();
      
      // Check if user is admin (admins have unlimited credits)
      const isAdmin = currentUser?.role === 'admin';
      
      if (isAdmin) {
        return {
          canCreate: true,
          availableCredits: 999, // Unlimited credits for admin
          isCoordinator: true // Admin has coordinator privileges
        }
      }

      const isCoordinator = await this.isUserCoordinator(userEmail)
      const availableCredits = await this.getAvailableCredits(userEmail)

      return {
        canCreate: isCoordinator && availableCredits > 0,
        availableCredits,
        isCoordinator
      }
    } catch (error) {
      console.error('Error checking event creation ability:', error)
      return {
        canCreate: false,
        availableCredits: 0,
        isCoordinator: false
      }
    }
  }

  /**
   * Get upgrade cost
   */
  static getUpgradeCost(): number {
    return this.UPGRADE_COST
  }

  /**
   * Get currency
   */
  static getCurrency(): string {
    return this.CURRENCY
  }
} 